import './Quiz.scss'

import { useEffect, useState } from 'react'
import { Container, Row, Spinner } from 'react-bootstrap'
import { useParams } from 'react-router-dom'

import {
  getQuizDetails,
  getQuizzesForCourseLessonTopic
} from 'components/common/api/Quizzes'
import {
  EVENT_CATEGORY_QUIZ,
  EVENT_ACTION_PAGEVIEW,
  saveUserEvent
} from 'components/common/api/UserEvents'
import { getTopicPath } from 'components/common/helpers/Paths'
import QuestionSet from '../QuestionSet'
import QuizNavigation from '../QuizNavigation'
import QuizProgressBar from '../QuizProgressBar'
import {
  getLessonTopics,
  getTopicCacheKey
} from 'components/common/api/Courses'

/**
 * Quiz component. Used for displaying a Quiz.
 */
export default function Quiz({ setReturnPath, setHeaderTitle, cache }) {
  const params = useParams()

  const [quiz, setQuiz] = useState()
  const [quizzes, setQuizzes] = useState()
  const [questions, setQuestions] = useState()
  const [topics, setTopics] = useState()
  const [resultsMessages, setResultsMessages] = useState()
  const [activeQuestion, setActiveQuestion] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  const { quizId, courseId, lessonId, topicId } = params

  /**
   * Helper function to determine whether or not we should show quiz controls
   * @returns Boolean indicating whether or not we should show quiz controls
   */
  const shouldShowControls = () =>
    questions.length > 1 &&
    activeQuestion > -1 &&
    activeQuestion <= questions.length - 1

  /**
   * Helper function to determine whether or not we should show quiz results
   * @returns Boolean indicating whether or not we should show quiz results
   */
  const isSummaryScreen = () =>
    questions.length > 1 &&
    activeQuestion === questions.length &&
    resultsMessages

  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0)

    // Set return path
    setReturnPath(getTopicPath(courseId, lessonId, topicId))

    // Return early if we're missing any required params
    if (!courseId || !lessonId || !topicId || !quizId || quiz) return

    // Load the quiz
    async function getQuiz() {
      // Let's get all quizzes for this topic, so we can autoadvance to the next quiz if we complete this one
      const quizzes = await getQuizzesForCourseLessonTopic(
        courseId,
        lessonId,
        topicId
      )

      // Find the quiz we're looking for
      const quizResult = quizzes.length
        ? quizzes.find(quiz => quiz.id === parseInt(quizId))
        : null

      // Get the quiz details
      const { questions: quizQuestions, results_messages } =
        await getQuizDetails(quizId)

      // Check cache first
      const cacheKey = getTopicCacheKey(lessonId, topicId)
      const topicResult = await cache.tryGetAsyncResult(
        cacheKey,
        getLessonTopics,
        this,
        lessonId,
        courseId
      )
      const topic = topicResult.find(topic => topic.id === parseInt(topicId))

      // Set all the things
      setHeaderTitle(topic?.clean_title)
      setQuiz(quizResult)
      setQuestions(quizQuestions)
      setQuizzes(quizzes)
      setTopics(topicResult)
      setResultsMessages(results_messages)
      setIsLoading(false)

      // Log quiz access
      saveUserEvent(EVENT_CATEGORY_QUIZ, EVENT_ACTION_PAGEVIEW, quizId)
    }
    getQuiz()
  }, [
    courseId,
    lessonId,
    topicId,
    quizId,
    quiz,
    setReturnPath,
    setHeaderTitle,
    cache
  ])

  return (
    <>
      {isLoading || !quiz ? (
        <Row>
          <Spinner
            animation='border'
            role='status'
            className='app-spinner'
            variant='primary'
            size='lg'
          >
            <span className='visually-hidden'>Loading...</span>
          </Spinner>
        </Row>
      ) : (
        <>
          <Container
            fluid
            className={`quiz-container ${
              isSummaryScreen() ? 'results-summary' : ''
            }`}
          >
            <div
              className={`quiz-wrapper ${
                shouldShowControls() ? 'show-controls' : ''
              }`}
            >
              {shouldShowControls() && (
                <QuizProgressBar
                  completed={activeQuestion + 1}
                  total={questions.length}
                  hasSummary={!!resultsMessages}
                />
              )}
              <QuestionSet
                activeQuestion={activeQuestion}
                setActiveQuestion={setActiveQuestion}
                questions={questions}
                resultsMessages={resultsMessages}
                quiz={quiz}
                courseId={courseId}
                lessonId={lessonId}
                topicId={topicId}
                topics={topics}
                quizzes={quizzes}
              />
            </div>
            {shouldShowControls() && (
              <QuizNavigation
                activeQuestion={activeQuestion}
                setActiveQuestion={setActiveQuestion}
                questions={questions}
                hasSummary={!!resultsMessages}
              />
            )}
          </Container>
        </>
      )}
    </>
  )
}
